import {Injectable} from '@angular/core'
import {BehaviorSubject, Observable, of} from "rxjs";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {ApiUrl} from "../../shared/models/apiurl.models";
import {map, tap} from "rxjs/operators";
import {Router} from "@angular/router";
import {TokenStore} from "../../services/token-store";
import {UserStorageService} from "../../services/UserStorageService";

export interface NotificationPayload {
    [key: string]: any; // adjust if you know exact structure
}

export interface Notification {
    id: number;
    userId?: number;
    orderId?: number;

    status?: string;
    createdAt?: string;
    image?: string;
    type?: string;

    subject?: string;
    message?: string;
    payload?: any;

    data?: NotificationPayload;

    is_read: boolean;
    flag: boolean;

    showFull?: boolean; // UI helper only
}

@Injectable()
export class NotificationBellService {

    // No Authorization header here: the old one was captured once at
    // construction (empty for a guest, and still empty after a later login,
    // which also stopped the interceptor from attaching the real token).
    // AppInterceptor attaches the current in-memory token to every request.
    private options = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json'
        })
    }

    messages$: Observable<Notification[]>;

    private unreadCountSubject = new BehaviorSubject<number>(0);
    unreadMessagesCount$= this.unreadCountSubject.asObservable();

    constructor(private http: HttpClient, private apuUrl: ApiUrl, public router:Router,
                private userStorage: UserStorageService) {
        this.notifyNotifications();
        // Guests skip the calls above; load them once the user signs in.
        this.userStorage.loggedIn$.subscribe(() => this.notifyNotifications());
    }

    /** notification-api endpoints require auth - never call them for a guest. */
    private get loggedIn(): boolean {
        return TokenStore.isLoggedIn();
    }
    notifyNotifications(){
        this.messages$ = this.getNotifications();
        this.getUnreadNotification();
    }
    getNotifications(): Observable<Notification[]> {
        if (!this.loggedIn) {
            return of([]);
        }
        const notification$ = this.http.get<Notification[]>(this.apuUrl.getNotificationsUrl(),  this.options);
        return notification$.pipe(
            map(product => product["results"])
        );
    }
    getUnreadNotification(): void {
        if (!this.loggedIn) {
            this.unreadCountSubject.next(0);
            return;
        }
        this.http
            .get<any>(this.apuUrl.getUnreadCountNotificationsUrl(), this.options)
            .pipe(
                map(res => res["unread_messages_count"])
            )
            .subscribe(count => {
                this.unreadCountSubject.next(count);
            });
    }

    markAsRead(notificationId: number): Observable<any> {
        return this.http.post(
            this.apuUrl.markAsReadUrl(),   // create this URL method
            { id: notificationId },
            this.options
        ).pipe(
            tap(() => {
                // 🔥 Refresh unread counter
                this.getUnreadNotification();
            })
        );
    }
    raiseFlag(notificationId: number): Observable<any> {
        return this.http.post(
            this.apuUrl.raiseFlagUrl(),   // create this URL method
            { id: notificationId },
            this.options
        );
    }

    handle(payload: NotificationPayload): void {

        switch (payload.action) {

            case 'navigate':
                this.handleNavigate(payload);
                break;

            case 'open_url':
                this.openExternal(payload.url);
                break;

            default:
                this.openExternal(payload.url);
        }
    }

    private handleNavigate(payload: NotificationPayload): void {

        switch (payload.route) {

            case 'product_details':
                this.router.navigate(
                    ['/single', payload.params?.product_id]
                );
                break;

            // case 'chat':
            //     const roomId = Number(payload.params?.room_id);
            //
            //     if (!isNaN(roomId)) {
            //         // Example: open chat component or popup
            //         this.router.navigate(['/chat'], {
            //             queryParams: { roomId }
            //         });
            //     }
            //     break;

            case 'home':
                this.router.navigate(['/home']);
                break;

            default:
                this.openExternal(payload.url);
        }
    }

    private openExternal(url?: string): void {
        if (url) {
            window.open(url, '_blank');
        }
    }
}